import { MultiSelectComponent } from 'ng-multiselect-dropdown';

/**
 * Ordenação A-Z padrão de todos os combos do sistema.
 *
 * - <select> nativo: ordenado no DOM por um MutationObserver global (installNativeSelectSort).
 *   Exceção: <select data-no-sort> mantém a ordem original (meses, anos, períodos fixos...).
 * - ng-multiselect-dropdown: ordenado na exibição (patchMultiSelectDropdownSort).
 *   Exceção: settings com noSort: true.
 * - app-searchable-select: ordena no próprio componente. Exceção: [sortOptions]="false".
 *
 * Escalas (importância, urgência, periodicidade) seguem a própria ordem automaticamente (ver COMBO_SCALES).
 * Placeholders ("Selecione...", "Todos", opção vazia/null) ficam sempre no topo.
 * A comparação é pt-BR, sem diferenciar acento/caixa e numérica ("2" antes de "10").
 */

declare module 'ng-multiselect-dropdown/multiselect.model' {
  interface IDropdownSettings {
    /** Mantém a ordem original dos itens (meses...). */
    noSort?: boolean;
  }
}

const comboCollator = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });

export function compareComboLabels(left: unknown, right: unknown): number {
  return comboCollator.compare(String(left ?? '').trim(), String(right ?? '').trim());
}

/**
 * Escalas com ordem própria. Um combo cujos rótulos batem com uma escala segue essa ordem
 * em vez de A-Z; rótulos desconhecidos vão para o fim, em A-Z.
 */
const COMBO_SCALES: readonly string[][] = [
  ['Baixa', 'Média', 'Alta', 'Estratégica', 'Crítica'], // importância
  ['Baixa', 'Moderada', 'Alta', 'Imediata', 'Emergencial'], // urgência
  ['Diária', 'Semanal', 'Quinzenal', 'Mensal', 'Bimestral', 'Trimestral', 'Semestral', 'Anual'] // periodicidade
].map((scale) => scale.map(normalizeComboLabel));

function normalizeComboLabel(label: unknown): string {
  return String(label ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
}

function findScale(labels: readonly string[]): readonly string[] | null {
  let best: readonly string[] | null = null;
  let bestMatches = 0;
  for (const scale of COMBO_SCALES) {
    const matches = labels.filter((label) => scale.includes(label)).length;
    if (matches > bestMatches) {
      best = scale;
      bestMatches = matches;
    }
  }
  // Pelo menos 2 rótulos e metade do combo precisam pertencer à escala
  return bestMatches >= 2 && bestMatches * 2 >= labels.length ? best : null;
}

/** "Outra/Outro/Outros/Outras" é a opção genérica: fica sempre no fim do combo. */
const CATCH_ALL = new Set(['outra', 'outro', 'outros', 'outras']);

export function isCatchAllLabel(label: unknown): boolean {
  return CATCH_ALL.has(normalizeComboLabel(label));
}

export function sortComboItems<T>(items: readonly T[], getLabel: (item: T) => unknown): T[] {
  const scale = findScale(items.map((item) => normalizeComboLabel(getLabel(item))));
  const rank = (item: T): number => {
    if (isCatchAllLabel(getLabel(item))) return Number.MAX_SAFE_INTEGER;
    const index = scale ? scale.indexOf(normalizeComboLabel(getLabel(item))) : -1;
    return index === -1 ? Number.MAX_SAFE_INTEGER - 1 : index;
  };

  return [...items].sort((left, right) => rank(left) - rank(right) || compareComboLabels(getLabel(left), getLabel(right)));
}

const PLACEHOLDER_TEXT = /^(selecion|escolha|todos|todas|nenhum|nenhuma|choose|select)/i;
// [ngValue]="null" é renderizado pelo Angular como "0: null"
const EMPTY_VALUE = /^(|null|undefined|\d+:\s*(null|undefined))$/;

function isPlaceholderOption(option: HTMLOptionElement): boolean {
  const text = option.text.trim();
  return EMPTY_VALUE.test(option.value.trim()) || !text || PLACEHOLDER_TEXT.test(text);
}

function sortOptionsIn(container: HTMLSelectElement | HTMLOptGroupElement): void {
  const options = Array.from(container.children).filter(
    (child): child is HTMLOptionElement => child instanceof HTMLOptionElement
  );
  if (options.length < 2) {
    return;
  }

  const pinned = options.filter(isPlaceholderOption);
  const sorted = [...pinned, ...sortComboItems(options.filter((o) => !isPlaceholderOption(o)), (o) => o.text)];
  if (sorted.every((option, index) => option === options[index])) {
    return; // já ordenado: evita mutações (e loop com o observer)
  }

  // Reinsere na nova ordem a partir da posição da primeira opção (mantém comentários/âncoras do Angular no lugar)
  const anchor = options[options.length - 1].nextSibling;
  sorted.forEach((option) => container.insertBefore(option, anchor));
}

export function sortSelectElement(select: HTMLSelectElement): void {
  if (select.hasAttribute('data-no-sort')) {
    return;
  }

  // Mover <option> no DOM pode fazer o browser trocar a seleção visível; preserva o que estava selecionado.
  const selected = select.multiple ? null : select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;

  sortOptionsIn(select);
  Array.from(select.children)
    .filter((child): child is HTMLOptGroupElement => child instanceof HTMLOptGroupElement)
    .forEach((group) => sortOptionsIn(group));

  if (!select.multiple) {
    if (selected) {
      selected.selected = true;
    } else {
      select.selectedIndex = -1;
    }
  }
}

function collectSelects(node: Node, into: Set<HTMLSelectElement>): void {
  const element = node instanceof Element ? node : node.parentElement;
  if (!element) {
    return;
  }
  const owner = element.closest('select');
  if (owner) {
    into.add(owner);
  }
  if (node instanceof Element) {
    node.querySelectorAll('select').forEach((select) => into.add(select));
  }
}

export function installNativeSelectSort(root: Node = document.body): void {
  const pending = new Set<HTMLSelectElement>();
  let scheduled = false;

  const flush = () => {
    scheduled = false;
    const selects = Array.from(pending);
    pending.clear();
    selects.forEach((select) => select.isConnected && sortSelectElement(select));
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      collectSelects(mutation.target, pending);
      mutation.addedNodes.forEach((node) => collectSelects(node, pending));
    }
    if (pending.size && !scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  });

  observer.observe(root, { childList: true, subtree: true, characterData: true });
  root instanceof Element && root.querySelectorAll('select').forEach((select) => pending.add(select));
  flush();
}

/**
 * O ng-multiselect-dropdown guarda os itens em `_data` (sempre reatribuído, nunca mutado) e o template
 * itera sobre ele. Interceptamos `_data` para devolver a lista ordenada, com cache por referência.
 */
export function patchMultiSelectDropdownSort(): void {
  const proto = MultiSelectComponent.prototype as any;
  if (proto.__comboSortPatched) {
    return;
  }
  proto.__comboSortPatched = true;

  Object.defineProperty(proto, '_data', {
    configurable: true,
    get(this: any) {
      const raw: any[] = this.__comboSortRaw ?? [];
      if (this._settings?.noSort) {
        return raw;
      }
      if (this.__comboSortFrom !== raw) {
        this.__comboSortFrom = raw;
        this.__comboSortSorted = sortComboItems(raw, (item) => item?.text);
      }
      return this.__comboSortSorted;
    },
    set(this: any, value: any[]) {
      this.__comboSortRaw = value;
    }
  });
}
